import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  filter,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { SuccessResponse } from 'src/app/model/success-response.model';
import { InitFacade } from 'src/app/store/facade/init.facade';
import * as userManagementActions from 'src/app/store/user-management/actions/user-management.actions';
import * as actions from '../actions/dashboard.actions';
import { DashboardService } from '../dashboard.service';
import { DashboardFacade } from '../facade/dashboard.facade';
import {
  BalancesOverviewResponse,
  RecentTransactionsResponse,
  SearchedContact,
} from '../state/dashboard.state';

@Injectable({
  providedIn: 'root',
})
export class DashboardEffects {
  constructor(
    private actions$: Actions,
    private facade: DashboardFacade,
    private initFacade: InitFacade,
    private service: DashboardService,
  ) {}

  getSearchedContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getSearchContacts),
      withLatestFrom(this.initFacade.accountInfo$),
      map(([action, accountInfo]) => ({
        searchedContact: action.searchedContact,
        userId: accountInfo.custId,
      })),
      tap((action) => {
        if (action && !action.searchedContact) {
          this.facade.resetSearchedContacts();
        }
      }),
      filter((action) => !!action.searchedContact),
      switchMap((action) => {
        return this.service
          .getSearchContacts(action.searchedContact, action.userId)
          .pipe(
            map((searchedContacts: SearchedContact[]) =>
              actions.getSearchContactsSuccess({ searchedContacts }),
            ),
            catchError(() => of(actions.getSearchContactsError())),
          );
      }),
    ),
  );

  addChosenContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addChosenContact),
      switchMap((action) =>
        this.service.addChosenContact(action.payload).pipe(
          map((response: SuccessResponse) => actions.addChosenContactSuccess()),
          catchError(() => of(actions.addChosenContactError())),
        ),
      ),
    ),
  );

  refreshAfterContactAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addChosenContactSuccess),
      concatMap(() => [
        userManagementActions.getNotifications({
          offsetLimit: 0,
          maxResults: 10,
          reload: true,
        }),
        userManagementActions.getContacts(),
      ]),
    ),
  );

  inviteChosenContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.inviteChosenContact),
      withLatestFrom(
        this.initFacade.accountInfo$,
        this.facade.searchedContact$,
      ),
      concatMap(([, accountInfo, searchedContact]) =>
        this.service
          .inviteChosenContact(accountInfo.custId, searchedContact)
          .pipe(
            map((response: SuccessResponse) =>
              actions.inviteChosenContactSuccess(),
            ),
            catchError(() => of(actions.inviteChosenContactError())),
          ),
      ),
    ),
  );

  getBalancesOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getBalancesOverview),
      withLatestFrom(this.initFacade.accountInfo$),
      switchMap(([, accountInfo]) =>
        this.service.getBalancesOverview(accountInfo.custId).pipe(
          map((balancesOverview: BalancesOverviewResponse) =>
            actions.getBalancesOverviewSuccess({ balancesOverview }),
          ),
          catchError(() => of(actions.getBalancesOverviewError())),
        ),
      ),
    ),
  );

  getRecentTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRecentTransactions),
      withLatestFrom(this.initFacade.accountInfo$),
      switchMap(([action, accountInfo]) =>
        this.service
          .getRecentTransactions(accountInfo.custId, action.selectedYear)
          .pipe(
            map((response: RecentTransactionsResponse) =>
              actions.getRecentTransactionsSuccess({ response }),
            ),
            catchError(() => of(actions.getRecentTransactionsError())),
          ),
      ),
    ),
  );
}
